import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Trash2, Loader2, Check, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import client from '../api/client';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { TAG_COLORS, tagChipClass } from '../lib/tagColors';

function TagRow({ tag, onChanged, onDeleted }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tag.name);
  const [color, setColor] = useState(tag.color);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const commit = async () => {
    const name = draft.trim();
    if (!name) { setEditing(false); setDraft(tag.name); return; }
    setSaving(true);
    try {
      await client.put(`/client-tags/${tag.id}`, { name, color });
      onChanged(tag.id, { name, color });
      setEditing(false);
    } catch {
      toast.error(t('clientTags.renameFailed', 'Rename failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await client.delete(`/client-tags/${tag.id}`);
      onDeleted(tag.id);
      toast.success(t('clientTags.deleted', 'Tag deleted'));
    } catch {
      toast.error(t('clientTags.deleteFailed', 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="py-2 border-b last:border-0 space-y-1.5">
      <div className="flex items-center gap-2">
        {editing ? (
          <Input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setEditing(false); setDraft(tag.name); }
            }}
            className="h-9 md:h-8 text-sm flex-1"
          />
        ) : (
          <span className={`px-2 py-0.5 rounded-full border text-xs ${tagChipClass(tag.color)}`}>{tag.name}</span>
        )}
        {!editing && <span className="flex-1" />}
        {/* usage count — how many clients carry this tag */}
        <span className="text-xs text-muted-foreground shrink-0">{tag.count ?? 0}</span>
        {editing ? (
          <Button size="icon" className="h-11 w-11 md:h-8 md:w-8 bg-primary hover:bg-[#e05200] shrink-0" onClick={commit} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
        ) : (
          <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8 text-muted-foreground hover:text-foreground" onClick={() => setEditing(true)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8 text-destructive"
          onClick={() => setConfirmOpen(true)} disabled={deleting} data-testid={`delete-tag-${tag.id}`}>
          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </Button>

        {/* Deleting cascades — the tag is detached from every client carrying it. */}
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('clientTags.deleteTitle', 'Delete this tag?')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('clientTags.deleteWarning', {
                  defaultValue: '"{{name}}" will be removed from {{count}} client(s). This cannot be undone.',
                  name: tag.name,
                  count: tag.count ?? 0,
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive hover:bg-destructive/90"
                onClick={handleDelete} data-testid={`confirm-delete-tag-${tag.id}`}>
                {t('common.delete', 'Delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {editing && (
        <div className="flex gap-1 flex-wrap">
          {TAG_COLORS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              aria-label={c}
              className={`h-5 w-5 rounded-full border ${tagChipClass(c)} ${color === c ? 'ring-2 ring-primary' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ManageTagsModal({ open, onClose, tags: propTags, onTagsChanged }) {
  const { t } = useTranslation();
  const [tags, setTags] = useState(propTags);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('slate');
  const [adding, setAdding] = useState(false);

  useEffect(() => { setTags(propTags); }, [propTags]);

  const handleChanged = (id, patch) => {
    setTags(prev => prev.map(x => (x.id === id ? { ...x, ...patch } : x)));
    onTagsChanged();
  };
  const handleDeleted = (id) => {
    setTags(prev => prev.filter(x => x.id !== id));
    onTagsChanged();
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    if (tags.some(x => x.name.toLowerCase() === name.toLowerCase())) {
      toast.error(t('clientTags.alreadyExists', 'A tag with that name already exists'));
      return;
    }
    setAdding(true);
    try {
      const res = await client.post('/client-tags', { name, color: newColor });
      setTags(prev => [...prev, res.data]);
      setNewName('');
      onTagsChanged();
    } catch {
      toast.error(t('clientTags.createFailed', 'Could not create tag'));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('clientTags.manage', 'Manage Tags')}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1">
          {tags.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">{t('clientTags.none', 'No tags yet')}</p>
          )}
          {tags.map(tag => (
            <TagRow key={tag.id} tag={tag} onChanged={handleChanged} onDeleted={handleDeleted} />
          ))}
        </div>

        <div className="pt-3 border-t mt-1 space-y-1.5">
          <div className="flex gap-1 flex-wrap">
            {TAG_COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setNewColor(c)}
                aria-label={c}
                className={`h-5 w-5 rounded-full border ${tagChipClass(c)} ${newColor === c ? 'ring-2 ring-primary' : ''}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && add()}
              placeholder={t('clientTags.newPlaceholder', 'New tag...')}
              className="flex-1 text-sm"
            />
            <Button className="bg-primary hover:bg-[#e05200] shrink-0" onClick={add} disabled={adding}>
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.add', 'Add')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
